import { FunctionComponent, useCallback, useEffect, useRef } from 'react'

import { useQuery, useQueryClient } from '@tanstack/react-query'
import Script from 'next/script'

import config from '@config'
import { executeQuery as executeChunksQuery } from '@utils/aureus/queries/chunks'
import useCMPHasUserInteracted from '@hooks/useCMPHasUserInteracted'
import useGetPageIdentity from '@hooks/useGetPageIdentity'
import useHasAureusJsClientScriptInitialized from '@hooks/useHasAureusJsClientScriptInitialized'
import usePageMetadata from '@hooks/usePageMetadata'
import { JSONWidgetType as TeaserStandardHybridJSONWidgetType } from '@widgets/TeaserStandardHybrid'
import { JSONWidgetType as TeaserToiJSONWidgetType } from '@widgets/TeaserToi'

const {
  aureus: { clientScript, endpoint, fallbackEndpoint, clientUUID, apiKey },
  publication: { publication },
} = config

const AUREUS_CLIENT_CONFIG = {
  endpoint,
  fallbackEndpoint,
  clientUUID,
  apiKey,
  extraParameters: {
    reportDataSimplificationEvents: true,
    viewableImpressionEnabled: true,
  },
  libMode: 'WEB',
  //   blacklisting
  // - when using this mode subsequent API calls are automatically deduplicated (using blacklist) i.e. it provides
  //   guarantee that every subsequent fetched recommendation would not include previously rendered materials
  // - this works as long as the same `AureusClient` instance is reused
  //! Before enabling this feature, make sure that the `blackListedIds` are properly set in the `fetchBlickBitesRecommendations` function
  autoBlackListingEnabled: false,
}

type ChunkRecommendationInfo = {
  mainItem?: TeaserToiJSONWidgetType
  items: TeaserStandardHybridJSONWidgetType[]
}

export type ChunksRecommendationInfo = [
  ChunkRecommendationInfo,
  ChunkRecommendationInfo,
  ChunkRecommendationInfo,
  ChunkRecommendationInfo,
]

const getChunkItems = async (contentBlacklist?: string) => {
  const initialResponse = [
    { items: [] },
    { items: [] },
    { items: [] },
    { items: [] },
  ] as ChunksRecommendationInfo

  const queryResult = (await executeChunksQuery({ contentBlacklist })) ?? []

  return queryResult.reduce((acc, curr, currIndex) => {
    const shouldBeDisplayedAsToi = currIndex % 5 === 0
    const accIndex = Math.floor(currIndex / 5)

    if (shouldBeDisplayedAsToi) {
      const teaser = {
        ...curr.teasers.toi,
        articleId: curr.articleID,
        aureusOfferId: curr.aureusOfferId,
      }
      acc[accIndex].mainItem = teaser
    } else {
      acc[accIndex].items.push({
        ...curr.teasers.standardHybrid,
        articleId: curr.articleID,
        aureusOfferId: curr.aureusOfferId,
      })
    }

    return acc
  }, initialResponse)
}

const AureusManager: FunctionComponent = () => {
  const queryClient = useQueryClient()
  const hasUserInteracted = useCMPHasUserInteracted()
  const { isHomePage } = useGetPageIdentity()
  const { personalizationInfo } = usePageMetadata()

  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined)

  const hasAureusJsClientScriptInitialized =
    useHasAureusJsClientScriptInitialized()

  useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: ['chunks'],
    queryFn: () => getChunkItems(personalizationInfo?.contentBlacklist),
    enabled:
      publication !== 'romandie' &&
      isHomePage &&
      hasAureusJsClientScriptInitialized,
    gcTime: 0,
  })

  const initAureusClient = useCallback(() => {
    window.aureusClient = new window.AureusClient({
      config: AUREUS_CLIENT_CONFIG,
    })

    queryClient.setQueryData(['has-aureus-js-client-script-initialized'], true)
    queryClient.invalidateQueries({
      queryKey: ['has-aureus-js-client-script-initialized'],
      exact: true,
    })

    clearTimeout(timeoutRef.current)
  }, [queryClient])

  const onTimeout = useCallback(() => {
    if (
      !queryClient.getQueryData(['has-aureus-js-client-script-initialized'])
    ) {
      console.warn(
        `Timeout of 3000ms exceeded. Init AureusClient without EventsAPI.`,
      )

      initAureusClient()
    }
  }, [initAureusClient, queryClient])

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  const onAureusJsClientScriptLoaded = useCallback(() => {
    // we use window.dlApi.cmd to create AureusClient instance after Simetra is loaded
    window.dlApi.cmd.push(() => {
      initAureusClient()
    })

    timeoutRef.current = setTimeout(onTimeout, 3000)
  }, [initAureusClient, onTimeout])

  return hasUserInteracted ? (
    <Script
      id="AureusJsClientScript"
      strategy="afterInteractive"
      defer={true}
      src={clientScript}
      onLoad={onAureusJsClientScriptLoaded}
    />
  ) : null
}

export default AureusManager
